// Pre-create check (atomic-design Rule 3): considered `ApproachTreeMolecule` and
// `ContextRootInspectorMolecule`. Both render a resolved tree, but neither models an
// EXECUTION result — the loading / error / empty triple of a context the user just ran,
// with per-level stat cards above it. Extending either would have meant adding an execution
// lifecycle to a component whose contract is a settled tree. This composes them instead:
// `ApproachTreeNodeAtom` for the nodes, `DialogWrapper`, `Loader`, `MessageAtom`,
// `EmptyStateAtom` and `StatCardRowMolecule` for the shell.
import React from 'react';
import { useTranslation } from 'localization';
import type { ContextTreeExecutionResult, SphereNode } from 'types/context';
import DialogWrapper from '../DialogWrapper/DialogWrapper';
import Loader from '../../atoms/loader/loader';
import MessageAtom from '../../atoms/Message/MessageAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import ApproachTreeNodeAtom from '../../atoms/ApproachTreeNode/ApproachTreeNodeAtom';
import StatCardRowMolecule from '../StatCardRow/StatCardRowMolecule';
import './ContextPreviewPanelMolecule.scss';

export interface ContextPreviewPanelMoleculeProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  /** Localized error message — already resolved by the caller (403/400/network). */
  error: string | null;
  result: ContextTreeExecutionResult | null;
}

// Recurses SphereNode.children unconditionally — the preview's own `limit` (default 50)
// bounds tree size, so there is no need for a collapse/expand affordance the way the
// full Atlas tree needs one for arbitrarily large graphs.
function renderNodes(nodes: SphereNode[], level: number): React.ReactNode {
  return nodes.map((node) => (
    <React.Fragment key={node.id}>
      <ApproachTreeNodeAtom
        id={node.id}
        label={node.label || node.id}
        icon={node.glyph}
        level={level}
        expandable={Boolean(node.children?.length)}
        expanded
        data-testid={`context-preview-node-${node.id}`}
      />
      {node.children && node.children.length > 0 && renderNodes(node.children, level + 1)}
    </React.Fragment>
  ));
}

/**
 * Renders the result of `executeContextDefinition` (an unsaved definition run against a
 * picked sphere) as stats + a read-only tree. Composes `ApproachTreeNodeAtom` — the
 * generic id/label/icon/children node renderer — rather than the saved-context-bound
 * Atlas tree, which fetches its own data by context system name (EAINT-862 gap analysis
 * item 14).
 */
const ContextPreviewPanelMolecule: React.FC<ContextPreviewPanelMoleculeProps> = ({
  open,
  onClose,
  loading,
  error,
  result,
}) => {
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <DialogWrapper
      header={t('context.editor.preview.title')}
      visible={open}
      onHide={onClose}
      className="context-preview-panel-molecule"
      data-testid="context-preview-panel"
    >
      <div className="context-preview-panel-molecule__body">
        {loading && (
          <div className="context-preview-panel-molecule__loading" data-testid="context-preview-loading">
            <Loader />
            <span>{t('context.editor.preview.loading')}</span>
          </div>
        )}

        {!loading && error && (
          <MessageAtom
            severity="error"
            text={error}
            data-testid="context-preview-error"
          />
        )}

        {!loading && !error && result && (
          <>
            <StatCardRowMolecule
              className="context-preview-panel-molecule__stats"
              ariaLabel={t('context.editor.preview.title')}
              stats={[
                {
                  label: t('context.editor.preview.stats.totalLoaded'),
                  value: result.stats.totalLoaded,
                },
                {
                  label: t('context.editor.preview.stats.executionTimeMs'),
                  value: t('context.editor.preview.stats.executionTimeMsValue', {
                    ms: result.stats.executionTimeMs,
                  }),
                },
              ]}
            />
            {result.roots.length > 0 ? (
              <div
                className="context-preview-panel-molecule__tree"
                role="tree"
                aria-label={t('context.editor.preview.title')}
                data-testid="context-preview-tree"
              >
                {renderNodes(result.roots, 0)}
              </div>
            ) : (
              <EmptyStateAtom
                title={t('context.editor.preview.emptyTitle')}
                description={t('context.editor.preview.emptyDescription')}
              />
            )}
          </>
        )}

        {!loading && !error && !result && (
          <EmptyStateAtom
            title={t('context.editor.preview.emptyTitle')}
            description={t('context.editor.preview.emptyDescription')}
          />
        )}
      </div>
    </DialogWrapper>
  );
};

export default ContextPreviewPanelMolecule;
